'use client';

import { type FC } from 'react';
import { MessageRendererProps } from './MessageRenderer.types';
import styles from './MessageRenderer.module.css';
import { Icon } from '@/_components';
import { ThinkingIndicator } from './_components/ThinkingIndicator';
import { UserMessage } from './_components/UserMessage';
import { AssistantMessage } from './_components/AssistantMessage';
import { getClasses } from '@/_utils/classnames';

export const MessageRenderer: FC<MessageRendererProps> = ({
  messages,
  onExampleClick,
  streamingMessageId,
  onFeedback,
  messageFeedback,
  onRegenerate,
  onOpenQuestionsLibrary,
  thinkingStatus,
}) => {
  return messages.map((msg, index) => {
    const isUserMessage: boolean = msg.role === 'user' || msg.isUser === true;
    const isStreaming: boolean =
      !isUserMessage && msg.id === streamingMessageId;

    const messageRowClasses: string = getClasses(styles.messageRow, {
      [styles.messageRowUser]: isUserMessage,
    });

    const messageClasses: string = getClasses(styles.message, {
      [styles.userMessage]: isUserMessage,
      [styles.assistantMessage]: !isUserMessage,
      [styles.loadingMessage]: isStreaming,
    });

    const avatarClasses: string = getClasses(styles.messageAvatar, {
      [styles.userAvatar]: isUserMessage,
      [styles.assistantAvatar]: !isUserMessage,
    });

    return (
      <div
        key={index}
        className={messageRowClasses}
        data-role={msg.role}
        data-is-user={isUserMessage ? 'true' : 'false'}
      >
        {/* Avatar */}
        <div className={avatarClasses}>
          {isUserMessage ? (
            'U'
          ) : (
            <Icon name="sparkle" size={16} color="white" />
          )}
        </div>

        {/* Message Content */}
        <div className={styles.messageContent}>
          {/* Message bubble */}
          <div className={messageClasses}>
            {/* Message header inside bubble */}
            <div className={styles.messageHeader}>
              <div
                className={getClasses(styles.messageHeaderLeft, {
                  [styles.messageHeaderLeftUser]: isUserMessage,
                  [styles.messageHeaderLeftAssistant]: !isUserMessage,
                })}
              >
                <span
                  className={getClasses(styles.messageRole, {
                    [styles.messageRoleUser]: isUserMessage,
                    [styles.messageRoleAssistant]: !isUserMessage,
                  })}
                >
                  {isUserMessage ? 'You' : 'Admin Assistant'}
                </span>
                {!isUserMessage && isStreaming && (
                  <ThinkingIndicator status={thinkingStatus} />
                )}
              </div>
              <span
                className={getClasses(styles.messageTime, {
                  [styles.messageTimeUser]: isUserMessage,
                  [styles.messageTimeAssistant]: !isUserMessage,
                })}
              >
                {msg.time}
              </span>
            </div>

            <div className={styles.messageText}>
              {isUserMessage ? (
                <UserMessage message={msg} />
              ) : (
                <AssistantMessage
                  message={msg}
                  messages={messages}
                  onExampleClick={onExampleClick}
                  onFeedback={onFeedback}
                  messageFeedback={messageFeedback}
                  isStreaming={isStreaming}
                  onRegenerate={onRegenerate}
                  onOpenQuestionsLibrary={onOpenQuestionsLibrary}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  });
};
